import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Carousel, Spinner, Container } from 'react-bootstrap';
import { useParams, useLocation } from 'react-router-dom';
import request from '../../../../../request';
import DeviceActivator from './DeviceActivator';
import Notify from '../../../../compartidos/Notify';
import '../Turnero.css';
import { motion, AnimatePresence } from 'framer-motion';

// Icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt, faClock, faUserTag, faBullhorn, faVolumeUp, faDesktop } from '@fortawesome/free-solid-svg-icons';

/**
 * QueueDisplay - Customer View (Modern Split-Screen)
 */
const QueueDisplay = ({ ipserv }) => {
    const { type } = useParams(); // URL fallback param
    const location = useLocation();

    // Parse multiple types from query params (?types=CAJA,ATENCION)
    const queryParams = new URLSearchParams(location.search);
    const typesStr = queryParams.get('types');
    const types = typesStr ? typesStr.split(',') : [type];

    const [loading, setLoading] = useState(true);
    const [queueData, setQueueData] = useState({
        active: [], // Array of active turns from all sectors
        next: []    // Combined waiting turns
    });
    const [multimedia, setMultimedia] = useState([]);
    const [sucursalInfo, setSucursalInfo] = useState(null);
    const [error, setError] = useState(false);
    const [currentTime, setCurrentTime] = useState(new Date());
    const [isActivated, setIsActivated] = useState(!!localStorage.getItem('dm_installation_token'));
    const [lastTurnId, setLastTurnId] = useState(null);

    // Audio Ref for notification
    const audioRef = useRef(new Audio('https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3')); // Professional Ding


    // Identify Branch (URL > Storage)
    const getSucursalId = () => {
        const urlId = queryParams.get('sucursalId');
        if (urlId) {
            localStorage.setItem('dm_display_sucursal', urlId);
            return urlId;
        }
        return localStorage.getItem('dm_display_sucursal');
    };

    const branchId = getSucursalId();

    const loadBranchInfo = () => {
        if (!branchId || !isActivated) {
            if (!branchId) setError(true);
            setLoading(false);
            return;
        }

        request.Get(ipserv + 'databranchestotvis', { sucursalId: branchId }, r => {
            if (r.success) {
                setSucursalInfo(r.rows);
                setError(false);
            } else {
                setError(true);
            }
        });
    };

    // Update Clock
    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    // Fetch initial config and multimedia for each turn type
    useEffect(() => {
        if (!branchId || !isActivated) return;

        const fetchParams = () => {
            const promises = types.map(acronym =>
                new Promise((resolve) => {
                    request.Get(ipserv + 'getTurneroParamstotvis', {
                        typeAcronym: acronym,
                        sucursalId: branchId
                    }, r => {
                        resolve(r.success ? (r.data || []) : []);
                    });
                })
            );

            Promise.all(promises).then(results => {
                // Flatten and merge multimedia from all types
                const combinedMultimedia = results.flat();
                setMultimedia(combinedMultimedia);
            });
        };

        fetchParams();
    }, [typesStr, branchId, isActivated]);

    // Polling for queue updates
    useEffect(() => {
        if (!branchId || !isActivated) return;

        const fetchQueue = () => {
            // Concurrent requests for all monitored types
            const promises = types.map(acronym =>
                new Promise((resolve) => {
                    request.Get(ipserv + 'getQueueStatetotvis', {
                        typeAcronym: acronym,
                        sucursalId: branchId
                    }, r => {
                        resolve(r.success ? { acronym, data: r.data } : { acronym, data: null });
                    });
                }, 'json', err => {
                    console.error("Error detallado:", err);
                    Notify({ type: 'fail', text: err.msg || 'Error de conexión' });
                    setLoading(false);
                })
            );

            Promise.all(promises).then(results => {
                let allActive = [];
                let allNext = [];

                results.forEach(res => {
                    if (res.data) {
                        // Enrich with acronym info
                        const activeWithAcronym = (res.data.active || []).map(t => ({ ...t, acronym: res.acronym }));
                        const lastWithAcronym = (res.data.last || []).map(t => ({ ...t, acronym: res.acronym }));

                        allActive = [...allActive, ...activeWithAcronym];
                        allNext = [...allNext, ...lastWithAcronym];
                    }
                });

                // Sort active by ID (latest first)
                allActive.sort((a, b) => b.TurnoId - a.TurnoId);

                const currentTopTurn = allActive[0];
                if (currentTopTurn && lastTurnId && currentTopTurn.TurnoId > lastTurnId) {
                    audioRef.current.play().catch(e => console.log("Audio play blocked by browser", e));
                }
                if (currentTopTurn) setLastTurnId(currentTopTurn.TurnoId);

                setQueueData({
                    active: allActive.slice(0, 4), // Top 4 active
                    next: allNext.slice(0, 8)      // Top 8 waiting
                });
                setLoading(false);
            });
        };

        fetchQueue();
        const interval = setInterval(fetchQueue, 3000); // Poll every 3 seconds
        return () => clearInterval(interval);
    }, [typesStr, type, ipserv, branchId, isActivated]);

    useEffect(() => {
        loadBranchInfo();
    }, [branchId, isActivated]);

    if (error) {
        return (
            <div className="turnero-container d-flex align-items-center justify-content-center p-4">
                <Container>
                    <div className="animate-in text-center py-5">
                        <div className="mb-4" style={{ color: '#ef4444' }}>
                            <FontAwesomeIcon icon={faMapMarkerAlt} size="6x" className="mb-4 opacity-50" />
                            <h1 className="display-4 font-weight-bold" style={{ color: 'white' }}>PANTALLA NO CONFIGURADA</h1>
                        </div>
                        <p className="text-muted h4 mb-5 mx-auto" style={{ maxWidth: '600px' }}>
                            En esta pantalla no se ha definido una sucursal. Por favor, añada el parámetro
                            <code className="text-primary mx-2">?sucursalId=XXX</code> a la URL para configurarla del modo correspondiente.
                        </p>
                        <div className="turnero-card p-4 d-inline-block">
                            <code>DIAGNOSTICO: Missing sucursalId in Storage/URL</code>
                        </div>
                    </div>
                </Container>
            </div>
        );
    }

    return (
        <div className="turnero-container overflow-hidden">
            {!isActivated && branchId && (
                <DeviceActivator
                    sucursalId={branchId}
                    onActivated={() => setIsActivated(true)}
                />
            )}

            {/* NEW PREMIUM HEADER */}
            <div className="display-premium-header">
                <div className="d-flex align-items-center">
                    <div className="display-logo-box">
                        <FontAwesomeIcon icon={faDesktop} className="mr-3 text-primary" />
                        TU <b>FILA</b>
                    </div>
                    <div className="sucursal-pill ml-4">
                        <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-2 opacity-50" />
                        {sucursalInfo || 'Cargando sucursal...'}
                    </div>
                </div>
                <div className="display-clock-box">
                    <FontAwesomeIcon icon={faClock} className="mr-3 text-primary opacity-50" />
                    {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </div>
            </div>

            <div className="turnero-split-premium">
                {/* Left Side: Multimedia (35%) */}
                <div className="display-multimedia-panel">
                    <div className="media-inner">
                        {multimedia.length > 0 ? (
                            <Carousel indicators={false} controls={false} interval={10000} fade className="h-100">
                                {multimedia.map((m, idx) => (
                                    <Carousel.Item key={idx} className="h-100">
                                        {m.MultimediaTipo === 'IMAGE' ? (
                                            <img src={m.UrlMultimedia} className="d-block w-100 h-100 object-fit-cover" alt="Ad" />
                                        ) : (
                                            <video autoPlay muted loop className="w-100 h-100 object-fit-cover">
                                                <source src={m.UrlMultimedia} type="video/mp4" />
                                            </video>
                                        )}
                                    </Carousel.Item>
                                ))}
                            </Carousel>
                        ) : (
                            <div className="media-placeholder-display h-100 d-flex align-items-center justify-content-center">
                                <div className="text-center opacity-30">
                                    <FontAwesomeIcon icon={faBullhorn} size="4x" className="mb-4" />
                                    <h2>CONTENIDO</h2>
                                    <p>PUBLICIDAD AQUÍ</p>
                                </div>
                            </div>
                        )}
                        <div className="media-vignette"></div>
                    </div>
                </div>

                {/* Right Side: Professional Queue (65%) */}
                <div className="display-queue-panel">
                    <div className="queue-panel-inner">
                        <div className="d-flex align-items-center mb-5 justify-content-between">
                            <h2 className="section-title-premium m-0">
                                <FontAwesomeIcon icon={faBullhorn} className="mr-3 text-primary" />
                                LLAMADOS RECIENTES
                            </h2>
                            <div className="volume-indicator">
                                <FontAwesomeIcon icon={faVolumeUp} />
                            </div>
                        </div>

                        <div className="active-turns-container">
                            <AnimatePresence mode="popLayout">
                                {queueData.active.length > 0 ? (
                                    queueData.active.map((t, idx) => (
                                        <motion.div
                                            key={t.TurnoId}
                                            layout
                                            initial={{ opacity: 0, x: 100, scale: 0.95 }}
                                            animate={{ opacity: 1, x: 0, scale: 1 }}
                                            exit={{ opacity: 0, scale: 0.9, x: -50 }}
                                            transition={{ duration: 0.5, type: 'spring' }}
                                            className={`active-turn-card-premium ${idx === 0 ? 'calling-now' : ''}`}
                                        >
                                            <div className="card-sector-tag">
                                                {t.TipoNombre || 'Tramite General'}
                                            </div>
                                            <div className="card-content-premium">
                                                <div className="turn-identification">
                                                    <div className="turn-acronym">{t.TipoAcronimo}</div>
                                                    <div className="turn-value">{t.TurnoNumero}</div>
                                                </div>
                                                <div className="client-data-premium">
                                                    <div className="client-label">NOM / DNI</div>
                                                    <div className="client-name">{t.TurnoNombreCliente || 'SIN DATOS'}</div>
                                                </div>
                                            </div>
                                            {idx === 0 && (
                                                <div className="calling-indicator">
                                                    <div className="calling-pulse"></div>
                                                    <span>SIGUIENTE</span>
                                                </div>
                                            )}
                                        </motion.div>
                                    ))
                                ) : (
                                    <div className="text-center py-5 opacity-50">
                                        <Spinner animation="grow" variant="primary" />
                                        <h4 className="mt-4">Sincronizando con el servidor...</h4>
                                    </div>
                                )}
                            </AnimatePresence>
                        </div>

                        <div className="queue-footer-premium">
                            <div className="footer-title">
                                <FontAwesomeIcon icon={faClock} className="mr-2" />
                                PRÓXIMOS EN ESPERA
                            </div>
                            <div className="upcoming-list-premium">
                                <AnimatePresence>
                                    {queueData.next.map((t, idx) => (
                                        <motion.div
                                            key={t.TurnoId}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: idx * 0.05 }}
                                            className="upcoming-pill-premium"
                                        >
                                            <span className="upcoming-acronym">{t.TipoAcronimo}</span>
                                            <span className="upcoming-number">{t.TurnoNumero}</span>
                                            <span className="upcoming-client">{t.TurnoNombreCliente || '---'}</span>
                                        </motion.div>
                                    ))}
                                </AnimatePresence>
                                {queueData.next.length === 0 && (
                                    <div className="text-center text-muted w-100 py-3 opacity-30">
                                        No hay más turnos en cola
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                .display-premium-header {
                    height: 80px;
                    background: #0f172a;
                    border-bottom: 1px solid rgba(255,255,255,0.05);
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 0 3rem;
                    box-shadow: 0 10px 30px rgba(0,0,0,0.5);
                    z-index: 100;
                    position: relative;
                }
                .display-logo-box {
                    font-size: 1.8rem;
                    font-weight: 300;
                    color: white;
                    letter-spacing: 2px;
                }
                .display-logo-box b { font-weight: 800; color: var(--turnero-primary); }
                .sucursal-pill {
                    background: rgba(255,255,255,0.03);
                    border: 1px solid rgba(255,255,255,0.08);
                    padding: 8px 18px;
                    border-radius: 30px;
                    color: #94a3b8;
                    font-weight: 700;
                }
                .display-clock-box {
                    font-size: 2rem;
                    font-weight: 800;
                    color: white;
                    font-family: monospace;
                }

                .turnero-split-premium {
                    display: flex;
                    height: calc(100vh - 80px);
                    width: 100vw;
                    background: #0f172a;
                }
                .display-multimedia-panel {
                    width: 35%;
                    height: 100%;
                    overflow: hidden;
                    position: relative;
                }
                .display-queue-panel {
                    flex: 1;
                    height: 100%;
                    background: radial-gradient(circle at top right, #1e293b 0%, #0f172a 100%);
                    padding: 3rem;
                    overflow: hidden;
                }
                .media-inner { position: relative; height: 100%; }
                .media-vignette {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    box-shadow: inset 0 0 150px rgba(0,0,0,0.8);
                    pointer-events: none;
                }

                .active-turn-card-premium {
                    background: rgba(255,255,255,0.03);
                    border: 1px solid rgba(255,255,255,0.05);
                    border-radius: 30px;
                    padding: 2.5rem;
                    margin-bottom: 2rem;
                    position: relative;
                    transition: all 0.5s ease;
                }
                .active-turn-card-premium.calling-now {
                    background: linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(30, 41, 59, 1) 100%);
                    border-color: var(--turnero-primary);
                    box-shadow: 0 20px 50px -10px rgba(99, 102, 241, 0.3);
                    transform: scale(1.02);
                }
                .card-sector-tag {
                    position: absolute;
                    top: -15px;
                    left: 40px;
                    background: var(--turnero-primary);
                    color: white;
                    padding: 8px 25px;
                    border-radius: 30px;
                    font-size: 1.1rem;
                    font-weight: 800;
                    text-transform: uppercase;
                    box-shadow: 0 5px 15px rgba(0,0,0,0.3);
                }
                .card-content-premium { display: flex; align-items: center; gap: 4rem; }
                .turn-identification { display: flex; align-items: end; gap: 1rem; }
                .turn-acronym { font-size: 2.5rem; font-weight: 300; opacity: 0.5; color: white; }
                .turn-value { font-size: 6rem; font-weight: 900; line-height: 1; color: white; }
                .client-data-premium { flex: 1; border-left: 2px solid rgba(255,255,255,0.1); padding-left: 4rem; }
                .client-label { font-size: 0.9rem; font-weight: 800; color: var(--turnero-primary); margin-bottom: 0.5rem; opacity: 0.8; }
                .client-name { font-size: 3rem; font-weight: 800; color: white; text-transform: uppercase; }

                .calling-indicator {
                    position: absolute;
                    right: 40px;
                    top: 50%;
                    transform: translateY(-50%);
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                }
                .calling-pulse {
                    width: 30px;
                    height: 30px;
                    background: #10b981;
                    border-radius: 50%;
                    box-shadow: 0 0 0 rgba(16, 185, 129, 0.4);
                    animation: pulse-green 2s infinite;
                    margin-bottom: 10px;
                }
                @keyframes pulse-green {
                    0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7); }
                    70% { transform: scale(1); box-shadow: 0 0 0 40px rgba(16, 185, 129, 0); }
                    100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
                }
                .calling-indicator span { font-size: 0.8rem; font-weight: 800; color: #10b981; letter-spacing: 2px; }

                .queue-footer-premium { margin-top: 3rem; }
                .footer-title { font-size: 1.1rem; font-weight: 800; color: #64748b; margin-bottom: 1.5rem; display: flex; align-items: center; }
                .upcoming-list-premium { display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px; }
                .upcoming-pill-premium {
                    background: rgba(255,255,255,0.02);
                    border: 1px solid rgba(255,255,255,0.05);
                    padding: 1rem 1.5rem;
                    border-radius: 15px;
                    display: flex;
                    align-items: center;
                    gap: 15px;
                }
                .upcoming-acronym { color: var(--turnero-primary); font-weight: 800; font-size: 1rem; }
                .upcoming-number { color: white; font-weight: 800; font-size: 1.2rem; }
                .upcoming-client { color: #64748b; font-size: 0.9rem; font-weight: 600; flex: 1; text-align: right; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }

                .object-fit-cover { object-fit: cover; }
            `}</style>
        </div>
    );
};

export default QueueDisplay;
